import {useMediaQuery} from "@mui/material";
import React, {createContext, useContext} from "react";

interface ScreenSizeConfig {
    micro?: number;
    sm?: number;
    md?: number;
    lg?: number;
}

interface ScreenSize {
    12: boolean,
    10: boolean,
    8: boolean,
    6: boolean,
    4: boolean,
    3: boolean,
    2: boolean
}


export interface GeneralContextType {
    screenSize: 'micro' | 'sm' | 'md' | 'lg',
    /**
     * Define o tamanho da tela e o tamanho do grid proporcional, quanto maior a tela menor o grid
     * 12 = 100% 6 = 50% 4 = 33% 3 = 25%
     */
    screenSizeLevel: 12 | 10 | 8 | 6 | 4 | 3 | 2;
    /**
     * Ajusta o tamanho do grid de acordo com a tela e um mapa de tamanhos
     * Ele irá pegar o valor mais proximo do tamanho da tela e o valor padrão,
     * Caso não tenha o valor padrão e nenhum dos valores proximos ele irá pegar o valor padrão do tamanho da tela
     * recomendação de uso: definir o tamanho "micro" com o "md" ou "lg" com "sm"
     * @see screenSizeLevel
     * @param p
     */
    adjustSizeLevel: (p: Partial<ScreenSizeConfig>) => number;
    /**
     * Faz a separação de um objeto, retornando apenas as chaves especificadas
     * @param obj objeto de onde serão retiradas as chaves
     * @param keys chaves que serão retiradas do objeto
     */
    pick: <T extends object, K extends keyof T>(obj: T, ...keys: K[]) => Pick<T, K>;
    /**
     * Compara dois objetos e verifica se todas chaves especificadas são iguais
     * @param obj1 primeiro objeto a ser comparado
     * @param obj2 segundo objeto a ser comparado
     * @param keys chaves que serão comparadas entre os dois objetos
     */
    pickCompare: <T, K extends keyof T>(obj1: T, obj2: T, keys: K[]) => boolean;
}

const GeneralContext = createContext<GeneralContextType | null>(null);

export function GeneralProvider({children}: { children: React.ReactNode }) {
    const sz: ScreenSize = {
        "12": useMediaQuery('(max-width: 360px)'),
        "10": useMediaQuery('(max-width: 480px)'),
        "8": useMediaQuery('(max-width: 600px)'),
        "6": useMediaQuery('(max-width: 960px)'),
        "4": useMediaQuery('(max-width: 1280px)'),
        "3": useMediaQuery('(max-width: 1440px)'),
        "2": useMediaQuery('(min-width: 1440px)'),
    };
    const levels: (keyof ScreenSize)[] = [12, 10, 8, 6, 4, 3, 2];
    const screenSizeLevel = levels.find(level => sz[level]) as 12 | 10 | 8 | 6 | 4 | 3 | 2;

    const screenSize = screenSizeLevel >= 10 ? 'micro' : screenSizeLevel >= 8 ? 'sm' : screenSizeLevel >= 6 ? 'md' : 'lg';
    const pick = <T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> => {
        return keys.reduce((acc, key) => {

            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                acc[key] = obj[key];
            }
            return acc;
        }, {} as Pick<T, K>);
    };
    const pickCompare = <T, K extends keyof T>(obj1: T, obj2: T, keys: K[]): boolean => {
        return keys.every(key => obj1[key] === obj2[key]);
    };

    const adjustSizeLevel = (p: ScreenSizeConfig) => {
        let s: number | undefined = undefined;
        if (screenSize == 'micro') s = p[screenSize] ?? p.sm ?? p.lg ?? screenSizeLevel;
        if (screenSize == 'sm') s = p[screenSize] ?? p.md ?? p.micro ?? screenSizeLevel;
        if (screenSize == 'md') s = p[screenSize] ?? p.sm ?? p.lg ?? screenSizeLevel;
        if (screenSize == 'lg') s = p[screenSize] ?? p.md ?? p.sm ?? screenSizeLevel;
        return s ?? screenSizeLevel;
    };
    return (
        <GeneralContext.Provider
            value={{
                screenSize,
                screenSizeLevel,
                adjustSizeLevel,
                pick,
                pickCompare,
            }}>
             {children}
        </GeneralContext.Provider>
    );
};

export const useGeneral = () => {
    const context = useContext(GeneralContext);
    if (!context) {
        throw new Error('useGeneral must be used within a GeneralContextProvider');
    }
    return context;
};